<template>
  <div class="stepper-content">
    <div class="step3-container step3-two-col">
      <v-row class="step3-row ma-0" no-gutters>
        <v-col cols="12" md="6" class="step3-left pa-3">
          <v-card class="step-card rounded-lg" outlined>
            <v-card-title class="section-title d-flex align-center">
              <span>Inputs</span>
              <v-spacer />
              <v-btn
                icon
                small
                outlined
                title="Mask options"
                @click="$emit('open-mask-options')"
              >
                <v-icon small>mdi-cog-outline</v-icon>
              </v-btn>
            </v-card-title>
            <v-divider class="section-divider" />
            <v-card-text class="section-body step3-body">
              <div class="step3-gallery-slots">
                <span class="step3-pick-slot-label">
                  {{ maskNumParts === 1 ? 'Select 1 image' : `Select ${maskNumParts} images (order = part 1, 2, 3)` }}
                </span>
                <ImageGallery
                  :items="mainMaskItems"
                  :value="maskGalleryValue"
                  :allow-multiple="maskNumParts > 1"
                  upload-button-align="right"
                  image-key="imageUrl"
                  :scroll-grid-only="true"
                  class="step3-gallery"
                  @input="$emit('mask-gallery-input', $event)"
                  @uploaded="$emit('mask-uploaded', $event)"
                  @nano-banana-deleted="$emit('nano-banana-deleted', $event)"
                >
                  <template #upload-row-actions>
                    <div class="engraving-action-btns d-flex align-center flex-wrap">
                      <v-badge
                        v-if="backsideEngravingCount >= 1"
                        :content="backsideEngravingCount"
                        :value="true"
                        color="primary"
                        overlap
                        class="step3-backside-badge-wrap"
                      >
                        <v-btn
                          small
                          outlined
                          class="gallery-upload-btn"
                          :disabled="!showBacksideButton"
                          @click="$emit('open-backside')"
                        >
                          <v-icon left small>mdi-text-box-outline</v-icon>
                          Backside engraving
                        </v-btn>
                      </v-badge>
                      <v-btn
                        v-else
                        small
                        outlined
                        class="gallery-upload-btn"
                        :disabled="!showBacksideButton"
                        @click="$emit('open-backside')"
                      >
                        <v-icon left small>mdi-text-box-outline</v-icon>
                        Backside engraving
                      </v-btn>
                      <v-badge
                        v-if="otherSvgsCount >= 1"
                        :content="otherSvgsCount"
                        :value="true"
                        color="primary"
                        overlap
                        class="step3-other-svgs-badge-wrap"
                      >
                        <v-btn
                          small
                          outlined
                          class="gallery-upload-btn"
                          :disabled="!showOtherSvgsButton"
                          @click="$emit('open-other-svgs')"
                        >
                          <v-icon left small>mdi-vector-square</v-icon>
                          Other SVGs
                        </v-btn>
                      </v-badge>
                      <v-btn
                        v-else
                        small
                        outlined
                        class="gallery-upload-btn"
                        :disabled="!showOtherSvgsButton"
                        @click="$emit('open-other-svgs')"
                      >
                        <v-icon left small>mdi-vector-square</v-icon>
                        Other SVGs
                      </v-btn>
                    </div>
                  </template>
                </ImageGallery>
                <div v-if="backsideEngravingItems.length" class="step3-backside-section">
                  <h4 class="step3-backside-heading">Backside engravings</h4>
                  <ImageGallery
                    :items="backsideEngravingItems"
                    :value="maskGalleryValue"
                    :allow-multiple="maskNumParts > 1"
                    :show-upload-button="false"
                    image-key="imageUrl"
                    class="step3-backside-gallery"
                    @input="$emit('mask-gallery-input', $event)"
                    @nano-banana-deleted="$emit('nano-banana-deleted', $event)"
                  />
                </div>
              </div>
              <p v-if="!maskImagesReady" class="text-body-2 text--secondary mt-3">
                Pick one image for each part of the shape to enable the mask and download.
              </p>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="6" class="step3-right pa-3">
          <v-card class="step-card rounded-lg outlined step3-orchestrator-card">
            <v-card-title class="section-title d-flex align-center">
              <span>Preview</span>
              <v-spacer />
              <v-btn
                v-if="maskImagesReady"
                icon
                small
                outlined
                :disabled="!maskDownloadReady"
                title="Download mask image"
                class="step3-download-btn"
                @click="onDownload"
              >
                <v-icon small>mdi-download</v-icon>
              </v-btn>
            </v-card-title>
            <v-divider class="section-divider" />
            <v-card-text class="section-body step3-orchestrator-body">
              <template v-if="maskPreviewVisible">
                <EngravingMaskEditor
                  ref="maskEditor"
                  :num-parts="maskNumParts"
                  :direction="maskDirection"
                  :shape="maskShape"
                  :output-size="maskOutputSize"
                  :spacing="maskSpacing"
                  :disable-file-upload="true"
                  :download-filename-prefix="orderNumber != null ? String(orderNumber) : ''"
                  @can-download-change="$emit('update:maskDownloadReady', $event)"
                />
              </template>
              <p v-else class="text-body-2 text--secondary">
                Select images on the left to see the mask preview here.
              </p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { ImageGallery } from '@/components/ImageGallery'

const EditorFallback = { name: 'MaskEditorFallback', render(h) { return h('div', { class: 'text-caption pa-2' }, 'Mask editor failed to load.') } }

export default {
  name: 'Step3Content',
  components: {
    ImageGallery,
    EngravingMaskEditor: () =>
      import('../EngravingMask/EngravingMaskEditor.vue').catch(() => EditorFallback),
  },
  methods: {
    onDownload() {
      if (this.$refs.maskEditor) this.$refs.maskEditor.download()
    },
    applyMaskImages(attempt = 0) {
      const editor = this.$refs.maskEditor
      if (editor) {
        editor.clearImage(null)
        const n = this.maskNumParts
        const tasks = []
        if (this.maskImage1Url) tasks.push(editor.setImageFromUrl(1, this.maskImage1Url))
        if (n >= 2 && this.maskImage2Url) tasks.push(editor.setImageFromUrl(2, this.maskImage2Url))
        if (n >= 3 && this.maskImage3Url) tasks.push(editor.setImageFromUrl(3, this.maskImage3Url))
        Promise.all(tasks)
          .then(() => {
            editor.redraw()
          })
          .catch(() => {})
        return
      }
      if (attempt < 10) {
        this.$nextTick(() => {
          requestAnimationFrame(() => this.applyMaskImages(attempt + 1))
        })
      }
    },
  },
  props: {
    maskNumParts: { type: Number, default: 1 },
    maskDirection: { type: String, default: 'horizontal' },
    maskShape: { type: String, default: 'circle' },
    maskSpacing: { type: Number, default: 0 },
    maskOutputSize: { type: Number, default: 512 },
    maskGalleryValue: { type: [String, Number, Array], default: null },
    allEditsForMaskItems: { type: Array, default: () => [] },
    backsideEngravingItems: { type: Array, default: () => [] },
    showBacksideButton: { type: Boolean, default: false },
    backsideEngravingCount: { type: Number, default: 0 },
    showOtherSvgsButton: { type: Boolean, default: false },
    otherSvgsCount: { type: Number, default: 0 },
    maskImagesReady: { type: Boolean, default: false },
    maskDownloadReady: { type: Boolean, default: false },
    maskImage1Url: { type: String, default: null },
    maskImage2Url: { type: String, default: null },
    maskImage3Url: { type: String, default: null },
    orderNumber: { type: [String, Number], default: '' },
  },
  computed: {
    /** Main pool for mask (edits + uploads); excludes backside so we show them in a separate section. */
    mainMaskItems() {
      const backsideIds = new Set((this.backsideEngravingItems || []).map((i) => i.id))
      return (this.allEditsForMaskItems || []).filter((item) => !backsideIds.has(item.id))
    },
    /** Show preview when all slots filled, or when numParts > 1 and at least one image selected. */
    maskPreviewVisible() {
      if (this.maskImagesReady) return true
      if (this.maskNumParts > 1 && (this.maskImage1Url || this.maskImage2Url || this.maskImage3Url)) return true
      return false
    },
  },
  watch: {
    maskImage1Url: 'applyMaskImages',
    maskImage2Url: 'applyMaskImages',
    maskImage3Url: 'applyMaskImages',
    maskNumParts: 'applyMaskImages',
    maskImagesReady: {
      handler(ready) {
        if (ready) this.$nextTick(() => this.applyMaskImages(0))
      },
    },
    maskPreviewVisible: {
      handler(visible) {
        if (visible) this.$nextTick(() => this.applyMaskImages(0))
      },
    },
  },
}
</script>

<style scoped>
.step3-container {
  padding: 0 28px;
  min-height: 320px;
}

.step3-two-col .step3-row {
  align-items: stretch;
}

.step3-left .step-card,
.step3-right .step-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: visible;
}

.step3-left .section-body,
.step3-right .section-body {
  flex: 1;
  min-height: 0;
  overflow: visible;
}

.step3-gallery-slots {
  display: flex;
  flex-direction: column;
}

.step3-gallery-slots {
  overflow: visible;
}

.step3-gallery-slots .step3-pick-slot-label {
  margin-bottom: 6px;
}

/* Scroll is on .gallery-grid-scroll inside ImageGallery (scrollGridOnly) so upload row + badge are not clipped */
.step3-gallery {
  min-height: 0;
  overflow: visible;
}

/* Shrink gallery grid to content so no empty space when few images */
.step3-gallery >>> .gallery-grid {
  height: auto;
  min-height: 0;
}

.step3-orchestrator-card >>> .step3-orchestrator-body {
  min-height: 380px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.step3-body {
  padding-bottom: 24px;
}

.step-card {
  background: rgba(255, 255, 255, 0.04) !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
}

.section-title {
  font-size: 0.9375rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  text-align: left;
  padding: 18px 20px 14px !important;
  color: rgba(255, 255, 255, 0.9) !important;
}

.section-divider {
  margin: 0 20px !important;
  border-color: rgba(255, 255, 255, 0.06) !important;
}

.section-body {
  text-align: left;
  padding: 18px 20px 20px !important;
  padding-top: 16px !important;
}

.step3-pick-slot-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.55);
}

.step3-backside-section {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.step3-backside-heading {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.6);
  margin: 0 0 12px;
  padding: 0;
  text-align: left;
}

.step3-backside-gallery {
  max-height: 12rem;
  overflow-y: auto;
}

.step3-backside-gallery >>> .gallery-grid {
  height: auto;
  min-height: 0;
}

/* Let badge overflow and match Step 1 appearance */
.step3-backside-badge-wrap {
  display: inline-block;
}

.step3-backside-badge-wrap >>> .v-badge__wrapper {
  overflow: visible;
}

.step3-backside-badge-wrap >>> .v-badge__badge {
  border-radius: 50% !important;
  min-width: 20px !important;
  height: 20px !important;
  padding: 0 6px !important;
  font-size: 0.75rem !important;
  font-weight: 700 !important;
  line-height: 20px !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.engraving-action-btns {
  gap: 8px;
}

.step3-other-svgs-badge-wrap {
  display: inline-block;
}

.step3-other-svgs-badge-wrap >>> .v-badge__wrapper {
  overflow: visible;
}

.step3-other-svgs-badge-wrap >>> .v-badge__badge {
  border-radius: 50% !important;
  min-width: 20px !important;
  height: 20px !important;
  padding: 0 6px !important;
  font-size: 0.75rem !important;
  font-weight: 700 !important;
  line-height: 20px !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
}
</style>
